import { ReactElement, memo } from 'react';
import useSWR from 'swr';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { Link } from '@appsumo/dorado-react';

import { ACCOUNT_DATA_URL } from '~/constants/accounts';
import { fetchJson } from '~/lib/fetch';
import useUser from '~/lib/user';
import { START_EMAIL_VERIFICATION_DATE } from '~/constants/global';
import { ACCOUNT_PASSWORD_RESET_TYPE } from '~/constants/user';
import { Loader } from '~/components/ui';
import { useEmailSettings } from '~/hooks/account';
import { AccountDataType, AccountEmailType } from '~/types/account';

export const IncompleteMessage = memo(
  ({
    isEmailVerified,
    isMissingPassword,
  }: {
    isEmailVerified: boolean;
    isMissingPassword: boolean;
  }) => {
    // Fetch account data in case it hasn't been fetched yet.
    const { data: accountData, mutate: mutateAccountData } = useSWR(
      ACCOUNT_DATA_URL,
      fetchJson,
    );

    const { onClickResendVerification, disableResend } =
      useEmailSettings(mutateAccountData);

    // accountData might be undefined until `useSWR` finishes fetching it.
    const emailData = (
      (accountData as AccountDataType) ?? {}
    ).emailaddress_list?.find((x) => x.primary);

    if (!isEmailVerified && isMissingPassword) {
      return (
        <div data-testid="account-complete-account-setup-msg">
          Before you can redeem your products, we&apos;ll need to confirm your
          email and set an account password.&nbsp;
          <Link href="/account/profile/" styled>
            Finish account setup
          </Link>
          .
        </div>
      );
    } else if (!isEmailVerified) {
      return (
        <div data-testid="account-verify-email-msg">
          Before you can redeem your products, we&apos;ll need to confirm your
          email.&nbsp;
          <button
            type="button"
            onClick={() =>
              onClickResendVerification(emailData as AccountEmailType)
            }
            disabled={disableResend}
            className="inline-block text-bolt"
          >
            <span>Resend email verification.</span>
            {disableResend && (
              <div className="inline-block">
                <Loader size="sm" className="ml-2" />
              </div>
            )}
          </button>
        </div>
      );
    }

    return (
      <div data-testid="account-set-password-msg">
        Complete your account setup by adding a password.&nbsp;
        <Link href="/account/password/update/" styled>
          Set your password
        </Link>
        .
      </div>
    );
  },
);

IncompleteMessage.displayName = 'IncompleteMessage';

export default function AccountIncompleteMessaging(): ReactElement {
  const { user } = useUser();
  const isEmailVerified = !!user?.email_verified;
  // is the users join date past our verification start date
  const pastVerificationDate =
    (user &&
      new Date(user.date_joined) > new Date(START_EMAIL_VERIFICATION_DATE)) ??
    false;
  const isMissingPassword = pastVerificationDate
    ? user?.require_password_reset === ACCOUNT_PASSWORD_RESET_TYPE.NEW_ACCOUNT
    : false;
  const showMessaging = isMissingPassword || !isEmailVerified;
  if (!showMessaging || !user) return <></>;

  return (
    <div className="my-4 rounded bg-lemon md:col-span-12">
      <div className="flex flex-row p-3">
        <FontAwesomeIcon
          icon={faExclamationTriangle}
          className="mr-2 self-center"
          height={24}
          width={24}
        />
        <div>
          <div>
            <b>Looks like your AppSumo account needs a little attention.</b>
          </div>
          <IncompleteMessage
            isEmailVerified={isEmailVerified}
            isMissingPassword={isMissingPassword}
          />
        </div>
      </div>
    </div>
  );
}
